import React from 'react';
import { Button } from '@ordr-as/umami-react/button';
import { useToast } from '@ordr-as/umami-react/toast';
import { useLocation } from '../../../../../context/LocationContext';
import { useNavigate } from 'react-router-dom';
import { useMutateAddOrderToDigitalBill } from '../../../../../hooks/useDigitalBillApi';
import FormattedMessage from '../../../../../components/FormattedMessage';
import { useCheckout } from '../../../../../context/CheckoutContext';
import type { SubmittedDigitalBillDto } from '../../../../../../generated-api';
import type { UnsettledDigitalBillDto } from '../../../../../types/digitalBill';
import { useScreenSize } from '../../../../../context/ScreenSizeContext';
import { useCartIsBusy } from '../../../../../hooks/useCartIsBusy';
import { usePinScreenSettings } from '../../../../../context/PinScreenSettingsContext';
import { useIntl } from 'react-intl';
import { createSelectUnsettledDigitalBillUrl } from '../../../../../utils/linkBuilder';
import { Userpilot } from 'userpilot';
import { useCartStore } from '../../../../../context/cart/CartStore';
import { useDigitalBillActions } from '../../../../../context/DigitalBillStore';

interface Props {
  activeDigitalBill: UnsettledDigitalBillDto;
}

const AddToDigitalBillButton: React.FC<Props> = ({ activeDigitalBill }) => {
  const { cart, hasItems, setAppliedDiscount, giftCardPhoneNumber, resetCart } = useCartStore((x) => ({
    cart: x.cart,
    giftCardPhoneNumber: x.giftCardPhoneNumber,
    hasItems: x.cart.orderLines.length > 0,
    resetCart: x.resetCart,
    setAppliedDiscount: x.applyDiscount,
  }));

  const { location } = useLocation();
  const cartIsBusy = useCartIsBusy();
  const { comment, setComment, setToDefaultDeliveryMethod, deliveryMethod } = useCheckout();
  const { setActiveDigitalBill } = useDigitalBillActions();
  const { mutate, isPending } = useMutateAddOrderToDigitalBill();
  const navigate = useNavigate();
  const { isPhone } = useScreenSize();
  const toasts = useToast();
  const { initiateChangeUserOnCompletedSale } = usePinScreenSettings();
  const { vatType } = useCheckout();
  const intl = useIntl();

  const handleAddToBillSuccess = (data: SubmittedDigitalBillDto) => {
    resetCart();
    setToDefaultDeliveryMethod();
    setComment(undefined);
    setActiveDigitalBill(undefined);
    setAppliedDiscount(undefined);

    if (isPhone) {
      navigate(`/${location.locationId}`);
    }

    toasts.pushSuccess({
      id: data.reference,
      message: (
        <div className="flex gap-0.5">
          <FormattedMessage id="checkout.addedTo" />
          &nbsp;
          <div
            className="underline cursor-pointer font-semibold"
            onClick={() => {
              navigate(createSelectUnsettledDigitalBillUrl(location.locationId, data.reference));
              toasts.remove(data.reference);
            }}
          >
            {intl.formatMessage({ id: 'checkout.table' }).toLowerCase()} {activeDigitalBill.primaryReference}
          </div>
        </div>
      ),
    });

    initiateChangeUserOnCompletedSale();
  };
  const handleAddToDigitalBill = () => {
    if (Userpilot) {
      Userpilot.track('epos_openbills_bill_add-to-bill');
    }

    if (cartIsBusy) {
      return;
    }

    mutate(
      {
        addOrderDto: {
          bookingOrderOrigin: 'EPOS_UNIBILL',
          cart,
          comment: comment || undefined,
          deliveryMethodId: deliveryMethod.id,
          deliveryPoint: { primaryReference: activeDigitalBill.primaryReference },
          giftCardPhoneNumber,
          vatType,
        },
        reference: activeDigitalBill.reference,
      },
      {
        onSuccess: handleAddToBillSuccess,
      }
    );
  };

  return (
    <div className="pb-4 px-4">
      <Button
        className="add-to-bill-from-cart"
        data-cy="add-to-bill"
        fullWidth
        kind="primary"
        onClick={handleAddToDigitalBill}
        isDisabled={isPending || !hasItems}
      >
        <FormattedMessage id="checkout.sendToDigitalBill" />
      </Button>
    </div>
  );
};

export default AddToDigitalBillButton;
